/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable max-statements */
/* eslint-disable id-length */
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useUnmount, useUpdateEffect } from 'react-use';
import { useRouteMatch } from 'react-router';

import { VideoCall, VideoCallApi } from '@dreryk/react-video-call';
import {
   appendTestableComponentAttributes,
   CloseFullscreenIcon,
   IconWrapper,
   Link,
   LoaderSpinner,
   LockIcon,
   LockOpenIcon,
   MinimizeIcon,
   OpenInFullIcon,
   Portal,
   StrongText,
   StyledFlex,
   useHistoryPushWithBackAction,
} from '@dreryk/react-components';
import { createInterpolatedTranslationKey, getCurrentLocale, useBoolean, useTranslate } from '@dreryk/react-common';
import {
   dispatchSessionAutoRefreshStartEvent,
   dispatchSessionAutoRefreshStopEvent,
} from '@dreryk/egabinet-security-frontend';

import { StyledMenuBar, StyledVideoConsultationWindow } from './VideoConsultationWindow.styled';
import { translationKeys } from './translations';
import { InCallState, useVideoConsultationGlobalState } from './shared/useVideoConsultationGlobalState';

const SAFE_PADDING_IN_PX = 8;
const WINDOW_BORDER_IN_PX = 4;
const MENUBAR_HEIGHT_IN_PX = 44;
const WINDOW_MIN_HEIGHT_IN_PX = 446;
const WINDOW_MIN_WIDTH_IN_PX = 462;
const VIDEO_RATIO = 3 / 4;
const WINDOW_HALF_SCREEN_RATIO = 0.5;
const WINDOW_QUATER_SCREEN_RATIO = 0.25;

type VideoConsultationWindowBaseProps = {
   state: InCallState;
   domain: string;
};

// eslint-disable-next-line max-lines-per-function
export const VideoConsultationWindowBase = (props: VideoConsultationWindowBaseProps): JSX.Element => {
   const { hangUp, unlockWindow, minimizeWindow, lockWindow } = useVideoConsultationGlobalState();
   const translate = useTranslate();
   const push = useHistoryPushWithBackAction(false);
   const backActionLocationMatch = useRouteMatch({ path: props.state.window.backAction?.location.path });

   const windowState = props.state.window;

   const [videoElement, setVideoElement] = useState<HTMLDivElement | null>(null);
   const videoCallApiRef = useRef<VideoCallApi | null>(null);
   const windowRef = useRef<HTMLDivElement>(null);
   const positionRef = useRef({ x: window.innerWidth, y: 60 });
   const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);
   const [{ setTrue: setWindowHalfSize, setFalse: setWindowQuarterSize }, windowHalfSize] = useBoolean(false);

   const updatePositionState = useCallback((newPosition: { x: number; y: number }) => {
      positionRef.current = newPosition;
      if (windowRef.current) {
         windowRef.current.style.left = `${newPosition.x}px`;
         windowRef.current.style.top = `${newPosition.y}px`;
      }
   }, []);

   const updatePositionWithinBounds = useCallback(() => {
      if (windowRef.current) {
         const windowElement = windowRef.current;
         const { documentElement } = document;

         const preferedWindowScreenRatio = windowHalfSize ? WINDOW_HALF_SCREEN_RATIO : WINDOW_QUATER_SCREEN_RATIO;
         const windowWidth = Math.floor(
            Math.max(document.body.clientWidth * preferedWindowScreenRatio, WINDOW_MIN_WIDTH_IN_PX)
         );
         const windowHeight = Math.floor(Math.max(windowWidth * VIDEO_RATIO, WINDOW_MIN_HEIGHT_IN_PX));

         windowElement.style.width = `${windowWidth}px`;
         windowElement.style.height = `${windowHeight}px`;

         if (videoElement) {
            const videoElementHeight = windowHeight - MENUBAR_HEIGHT_IN_PX;
            const videoElementWidth = windowWidth - WINDOW_BORDER_IN_PX;

            videoElement.style.height = `${videoElementHeight}px`;
            videoElement.style.width = `${videoElementWidth}px`;
            videoElement.style.overflow = 'hidden';
            videoElement.style.position = 'absolute';
            videoElement.style.inset = '0';
            videoElement.style.top = `${MENUBAR_HEIGHT_IN_PX}px`;
         }

         const maxX = documentElement.clientWidth - windowElement.offsetWidth - SAFE_PADDING_IN_PX;
         const maxY = documentElement.clientHeight - windowElement.offsetHeight - SAFE_PADDING_IN_PX;

         const currentPosition = positionRef.current;
         const newX = Math.max(SAFE_PADDING_IN_PX, Math.min(currentPosition.x, maxX));
         const newY = Math.max(SAFE_PADDING_IN_PX, Math.min(currentPosition.y, maxY));

         updatePositionState({ x: newX, y: newY });
      }
   }, [windowHalfSize, updatePositionState, videoElement]);

   const handleMouseDown = useCallback((event: React.MouseEvent) => {
      if (windowRef.current && event.currentTarget.parentElement === windowRef.current) {
         dragOffsetRef.current = {
            x: event.clientX - windowRef.current.getBoundingClientRect().left,
            y: event.clientY - windowRef.current.getBoundingClientRect().top,
         };
         windowRef.current.style.cursor = 'grabbing';
      }
   }, []);

   const handleMouseMove = useCallback(
      (event: MouseEvent) => {
         if (dragOffsetRef.current && windowRef.current) {
            const maxX = document.documentElement.clientWidth - windowRef.current.offsetWidth - SAFE_PADDING_IN_PX;
            const maxY = document.documentElement.clientHeight - windowRef.current.offsetHeight - SAFE_PADDING_IN_PX;

            let newX = event.clientX - dragOffsetRef.current.x;
            let newY = event.clientY - dragOffsetRef.current.y;

            newX = Math.max(SAFE_PADDING_IN_PX, Math.min(newX, maxX));
            newY = Math.max(SAFE_PADDING_IN_PX, Math.min(newY, maxY));

            updatePositionState({ x: newX, y: newY });
         }
      },
      [updatePositionState]
   );

   const handleMouseUp = useCallback(() => {
      dragOffsetRef.current = null;
      if (windowRef.current) {
         windowRef.current.style.cursor = 'grab';
      }
   }, []);

   const handleClickOutside = useCallback(() => {
      if (
         windowRef.current &&
         !windowState.locked &&
         !windowState.minimized &&
         !windowRef.current.contains(document.activeElement)
      ) {
         minimizeWindow();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [windowState.locked, windowState.minimized, minimizeWindow]);

   const handleBackAction = useCallback(() => {
      if (windowState.backAction) {
         push(windowState.backAction.location.path, windowState.backAction.location.state ?? {});
      }
   }, [push, windowState.backAction]);

   useLayoutEffect(() => {
      dispatchSessionAutoRefreshStartEvent();
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('click', handleClickOutside);
      window.addEventListener('resize', updatePositionWithinBounds);
      updatePositionWithinBounds();

      return () => {
         dispatchSessionAutoRefreshStopEvent();
         document.removeEventListener('mousemove', handleMouseMove);
         document.removeEventListener('mouseup', handleMouseUp);
         document.removeEventListener('click', handleClickOutside);
         window.removeEventListener('resize', updatePositionWithinBounds);
      };
   }, [handleClickOutside, handleMouseMove, handleMouseUp, updatePositionWithinBounds]);

   useLayoutEffect(() => {
      if (windowRef.current && !windowState.minimized) {
         windowRef.current.focus();
      }
   }, [windowState.minimized]);

   useUpdateEffect(updatePositionWithinBounds, [videoElement, windowHalfSize]);

   useUnmount(() => {
      videoCallApiRef.current?.hangup();
   });

   return (
      <Portal containerId={'videoConsultation'}>
         <StyledVideoConsultationWindow
            {...appendTestableComponentAttributes('VideoConsultationWindow', 'videoConsultation')}
            ref={windowRef}
            $visible={!windowState.minimized}
            $dragging={Boolean(dragOffsetRef.current)}
            // eslint-disable-next-line react/forbid-component-props
            style={{
               left: positionRef.current.x,
               top: positionRef.current.y,
               cursor: 'grab',
            }}
            tabIndex={0}
            autoFocus
         >
            <StyledMenuBar gap={12} alignItems={'end'} justifyContent={'space-between'} onMouseDown={handleMouseDown}>
               <StyledFlex gap={4} alignItems={'end'} justifyContent={'space-between'} flex={'1'}>
                  <StrongText testId={'title'} maxLines={1}>
                     {translate(
                        createInterpolatedTranslationKey(translationKeys.callWith, {
                           participantName: props.state.participantName,
                        })
                     )}
                  </StrongText>
                  {windowState.backAction && !backActionLocationMatch ? (
                     <Link testId={'back'} label={windowState.backAction.label} onClick={handleBackAction} nowrap />
                  ) : null}
               </StyledFlex>

               <StyledFlex gap={12} alignItems={'center'}>
                  {windowState.locked ? (
                     <IconWrapper testId={'lock'} onClick={unlockWindow}>
                        <LockIcon />
                     </IconWrapper>
                  ) : (
                     <IconWrapper testId={'lockOpen'} onClick={lockWindow}>
                        <LockOpenIcon />
                     </IconWrapper>
                  )}
                  {windowHalfSize ? (
                     <IconWrapper testId={'closeFull'} onClick={setWindowQuarterSize}>
                        <CloseFullscreenIcon />
                     </IconWrapper>
                  ) : (
                     <IconWrapper testId={'openInFull'} onClick={setWindowHalfSize}>
                        <OpenInFullIcon />
                     </IconWrapper>
                  )}
                  <IconWrapper testId={'minimize'} onClick={minimizeWindow}>
                     <MinimizeIcon />
                  </IconWrapper>
               </StyledFlex>
            </StyledMenuBar>
            <VideoCall
               domain={props.domain}
               roomName={props.state.roomName}
               token={props.state.token}
               getElement={setVideoElement}
               apiRef={videoCallApiRef}
               spinner={LoaderSpinner}
               onClose={hangUp}
               defaultLanguage={getCurrentLocale()}
            />
         </StyledVideoConsultationWindow>
      </Portal>
   );
};

type VideoConsultationWindowProps = {
   readonly domain: string;
};

export const VideoConsultationWindow = (props: VideoConsultationWindowProps): JSX.Element | null => {
   const { state } = useVideoConsultationGlobalState();

   if (state.type === 'idle') {
      return null;
   }

   return <VideoConsultationWindowBase state={state} domain={props.domain} />;
};
